import type { PurchaseAttachment, PurchaseLineView, PurchaseView, PurchasesReport, Supplier } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { del, errorMessage, get, post, put } from "../api";
import { ExportButtons } from "../components/ExportButtons";
import { type ReportTable, dollars } from "../components/exportReport";
import { Alert, Button, Dialog, Field, Loading, MoneyField, Panel, PanelHeader, SelectField, money, searchInputProps } from "../components/ui";

const fmtDate = (d: string | null) => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) : "No date");
const fmtNum = (n: number) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);
const strengthText = (l: { strengthPerMl: number | null; vialSizeMl: number | null }) =>
  [l.strengthPerMl !== null ? `${fmtNum(l.strengthPerMl)}/mL` : "", l.vialSizeMl !== null ? `${fmtNum(l.vialSizeMl)} mL` : ""].filter(Boolean).join(" × ");

/** Inventory → Purchases: every order, newest first. */
export function PurchasesList() {
  const list = useQuery({ queryKey: ["purchases"], queryFn: () => get<PurchaseView[]>("/api/purchases") });
  const [open, setOpen] = useState<string | null>(null);
  const [q, setQ] = useState("");
  if (list.error) return <Alert>{errorMessage(list.error)}</Alert>;
  if (!list.data) return <Loading />;
  const needle = q.trim().toLowerCase();
  const rows = list.data.filter(
    (p) => !needle || [p.supplier, p.invoiceNumber, p.orderNumber, ...p.lines.flatMap((l) => [l.name, l.lotNumber, l.supplierProductId])].some((x) => x.toLowerCase().includes(needle)),
  );
  return (
    <div className="flex flex-col gap-3">
      <input
        type="search"
        {...searchInputProps}
        aria-label="Search purchases"
        placeholder="Search by product, lot, invoice, or supplier"
        className="max-w-md rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-[15px]"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <Panel className="overflow-x-auto">
        <table className="table-base text-sm">
          <thead>
            <tr>
              <th>Date</th>
              <th>Supplier</th>
              <th>Invoice</th>
              <th>Products</th>
              <th className="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="cursor-pointer hover:bg-mist/50" onClick={() => setOpen(p.id)}>
                <td className="whitespace-nowrap">{fmtDate(p.orderedOn ?? p.receivedOn)}</td>
                <td className="whitespace-nowrap font-semibold">{p.supplier}</td>
                <td className="whitespace-nowrap">{p.invoiceNumber || <span className="text-muted">{p.orderNumber || "—"}</span>}</td>
                <td className="text-ink-soft">{p.lines.map((l) => (l.quantity === 1 ? l.name : `${l.name} ×${fmtNum(l.quantity)}`)).join(", ")}</td>
                <td className="text-right whitespace-nowrap tabular-nums">{p.totalCents !== null ? money(p.totalCents) : p.linesCents + p.extrasCents ? money(p.linesCents + p.extrasCents) : <span className="text-muted">No prices</span>}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={5} className="text-muted">
                  {needle ? "No matches." : "No purchases yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Panel>
      {open && <PurchaseDialog id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function PurchaseDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const p = useQuery({ queryKey: ["purchases", id], queryFn: () => get<PurchaseView>(`/api/purchases/${id}`) });
  const [editing, setEditing] = useState<"header" | PurchaseLineView | null>(null);
  const done = async (next: PurchaseView) => {
    qc.setQueryData(["purchases", id], next);
    await qc.invalidateQueries({ queryKey: ["purchases"], exact: true });
    await qc.invalidateQueries({ queryKey: ["inventory"] });
    setEditing(null);
  };
  const d = p.data;
  return (
    <Dialog title={d ? `${d.supplier}${d.invoiceNumber ? `, invoice ${d.invoiceNumber}` : ""}` : "Purchase"} onClose={onClose} wide>
      {p.error && <Alert>{errorMessage(p.error)}</Alert>}
      {!d ? (
        <Loading />
      ) : (
        <div className="flex flex-col gap-4">
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-muted">Order date</dt>
              <dd>{fmtDate(d.orderedOn)}</dd>
            </div>
            <div>
              <dt className="text-muted">Received</dt>
              <dd>{fmtDate(d.receivedOn)}</dd>
            </div>
            <div>
              <dt className="text-muted">Order #</dt>
              <dd>{d.orderNumber || "—"}</dd>
            </div>
            <div>
              <dt className="text-muted">Lines</dt>
              <dd className="tabular-nums">{money(d.linesCents)}</dd>
            </div>
            <div>
              <dt className="text-muted">Shipping</dt>
              <dd className="tabular-nums">{money(d.shippingCents)}</dd>
            </div>
            {d.feesCents > 0 && (
              <div>
                <dt className="text-muted">Fees</dt>
                <dd className="tabular-nums">{money(d.feesCents)}</dd>
              </div>
            )}
            {d.taxCents > 0 && (
              <div>
                <dt className="text-muted">Tax</dt>
                <dd className="tabular-nums">{money(d.taxCents)}</dd>
              </div>
            )}
            {d.discountCents > 0 && (
              <div>
                <dt className="text-muted">Discount</dt>
                <dd className="tabular-nums">−{money(d.discountCents)}</dd>
              </div>
            )}
            <div>
              <dt className="text-muted">Total charged</dt>
              <dd className="font-semibold tabular-nums">{d.totalCents === null ? "—" : money(d.totalCents)}</dd>
            </div>
            {d.notes && <div className="sm:col-span-3 text-ink-soft">{d.notes}</div>}
          </dl>
          {d.attachments.length > 0 && <Attachments purchaseId={d.id} files={d.attachments} />}
          <div>
            <Button variant="secondary" onClick={() => setEditing("header")}>
              Edit order details
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="table-base text-sm">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Strength × vial</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Price</th>
                  <th className="text-right" title="Price plus its share of shipping, fees, and tax, less discounts">Landed</th>
                  <th>Lot</th>
                  <th>BUD</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {d.lines.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <div className="font-semibold">{l.name}</div>
                      <div className="text-xs text-muted">
                        {l.productName !== l.name ? l.productName : ""}
                        {l.supplierProductId && ` #${l.supplierProductId}`}
                        {l.lotId ? " · on the shelf" : l.formularyItemId ? "" : " · not tracked"}
                      </div>
                    </td>
                    <td className="whitespace-nowrap">{strengthText(l) || "—"}</td>
                    <td className="text-right tabular-nums">{fmtNum(l.quantity)}</td>
                    <td className="text-right tabular-nums">{l.unitCents === null ? "—" : money(l.unitCents)}</td>
                    <td className="text-right tabular-nums">{l.landedUnitCents === null ? "—" : money(Math.round(l.landedUnitCents))}</td>
                    <td className="whitespace-nowrap">{l.lotNumber || "—"}</td>
                    <td className="whitespace-nowrap">{l.expiresOn ? fmtDate(l.expiresOn) : "—"}</td>
                    <td className="text-right">
                      <Button variant="quiet" onClick={() => setEditing(l)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {d.extrasCents !== 0 && d.linesCents > 0 && (
            <p className="text-sm text-muted">
              Landed is each price plus its share of {d.extrasCents > 0 ? `the ${money(d.extrasCents)} in shipping, fees, and tax` : `the ${money(-d.extrasCents)} in discounts`}, split by line cost. Reports use it for cost per drip.
            </p>
          )}
        </div>
      )}
      {d && editing === "header" && <HeaderDialog p={d} onDone={done} onClose={() => setEditing(null)} />}
      {d && editing && editing !== "header" && <LineDialog p={d} line={editing} onDone={done} onClose={() => setEditing(null)} />}
    </Dialog>
  );
}

function HeaderDialog({ p, onDone, onClose }: { p: PurchaseView; onDone: (v: PurchaseView) => void; onClose: () => void }) {
  const suppliers = useQuery({ queryKey: ["suppliers"], queryFn: () => get<Supplier[]>("/api/suppliers") });
  const [v, setV] = useState({
    supplierId: p.supplierId,
    invoiceNumber: p.invoiceNumber,
    orderNumber: p.orderNumber,
    orderedOn: p.orderedOn ?? "",
    receivedOn: p.receivedOn ?? "",
    shippingCents: p.shippingCents as number | null,
    discountCents: (p.discountCents || null) as number | null,
    taxCents: (p.taxCents || null) as number | null,
    feesCents: (p.feesCents || null) as number | null,
    totalCents: p.totalCents,
    notes: p.notes,
    attachments: p.attachments,
  });
  const save = useMutation({
    mutationFn: () =>
      put<PurchaseView>(`/api/purchases/${p.id}`, {
        ...v,
        orderedOn: v.orderedOn || null,
        receivedOn: v.receivedOn || null,
        shippingCents: v.shippingCents ?? 0,
        discountCents: v.discountCents ?? 0,
        taxCents: v.taxCents ?? 0,
        feesCents: v.feesCents ?? 0,
      }),
    onSuccess: onDone,
  });
  const attach = useMutation({
    mutationFn: async (file: File) => {
      const up = await post<{ key: string; url: string; headers: Record<string, string> }>("/api/purchases/files", { name: file.name, contentType: file.type });
      const res = await fetch(up.url, { method: "PUT", headers: up.headers, body: file });
      if (!res.ok) throw new Error("The file couldn't be uploaded. Try again.");
      return { key: up.key, name: file.name, contentType: file.type } satisfies PurchaseAttachment;
    },
    onSuccess: (a) => setV((cur) => ({ ...cur, attachments: [...cur.attachments, a] })),
  });
  return (
    <Dialog
      title="Order details"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {(save.error || attach.error) && <Alert>{errorMessage(save.error ?? attach.error)}</Alert>}
        <SelectField label="Supplier" value={v.supplierId} onChange={(e) => setV({ ...v, supplierId: e.target.value })}>
          {(suppliers.data ?? []).map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))}
        </SelectField>
        <Field label="Invoice #" maxLength={60} value={v.invoiceNumber} onChange={(e) => setV({ ...v, invoiceNumber: e.target.value })} />
        <Field label="Order #" maxLength={60} value={v.orderNumber} onChange={(e) => setV({ ...v, orderNumber: e.target.value })} />
        <Field label="Order date" type="date" value={v.orderedOn} onChange={(e) => setV({ ...v, orderedOn: e.target.value })} />
        <Field label="Received" type="date" value={v.receivedOn} onChange={(e) => setV({ ...v, receivedOn: e.target.value })} />
        <MoneyField label="Shipping" optional cents={v.shippingCents} onChange={(c) => setV({ ...v, shippingCents: c })} />
        <MoneyField label="Fees" optional cents={v.feesCents} onChange={(c) => setV({ ...v, feesCents: c })} hint="Cold pack, handling…" />
        <MoneyField label="Tax" optional cents={v.taxCents} onChange={(c) => setV({ ...v, taxCents: c })} />
        <MoneyField label="Discount" optional cents={v.discountCents} onChange={(c) => setV({ ...v, discountCents: c })} hint="Promo or credit" />
        <MoneyField label="Total charged" optional cents={v.totalCents} onChange={(c) => setV({ ...v, totalCents: c })} />
        <Field label="Notes" maxLength={1000} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} />
        <div className="flex flex-col gap-2 sm:col-span-2">
          <span className="text-sm font-semibold">Invoice files</span>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {v.attachments.map((a, i) => (
              <span key={a.key} className="inline-flex items-center gap-1 rounded-full bg-mist px-2.5 py-0.5">
                {a.name}
                <button type="button" className="text-muted hover:text-danger" aria-label={`Remove ${a.name}`} onClick={() => setV({ ...v, attachments: v.attachments.filter((_, j) => j !== i) })}>
                  ×
                </button>
              </span>
            ))}
            {v.attachments.length < 10 && (
              <label className="cursor-pointer font-medium text-brand-deep hover:underline">
                {attach.isPending ? "Uploading…" : "Attach a file"}
                <input type="file" hidden accept="application/pdf,image/png,image/jpeg,image/webp" disabled={attach.isPending} onChange={(e) => e.target.files?.[0] && attach.mutate(e.target.files[0])} />
              </label>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

/** The invoice files saved with a purchase; each opens in a new tab from a short-lived link. */
function Attachments({ purchaseId, files }: { purchaseId: string; files: PurchaseAttachment[] }) {
  const [error, setError] = useState<string | null>(null);
  const open = async (n: number) => {
    setError(null);
    const tab = window.open("", "_blank");
    try {
      const f = await get<{ url: string }>(`/api/purchases/${purchaseId}/files/${n}`);
      if (tab) tab.location.href = f.url;
      else window.location.assign(f.url);
    } catch (e) {
      tab?.close();
      setError(errorMessage(e));
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted">Invoice files:</span>
      {files.map((f, i) => (
        <button key={f.key} type="button" className="font-medium text-brand-deep underline-offset-4 hover:underline" onClick={() => void open(i)}>
          {f.name}
        </button>
      ))}
      {error && <span className="text-danger">{error}</span>}
    </div>
  );
}

function LineDialog({ p, line, onDone, onClose }: { p: PurchaseView; line: PurchaseLineView; onDone: (v: PurchaseView) => void; onClose: () => void }) {
  const [v, setV] = useState({ ...line, expiresOn: line.expiresOn ?? "" });
  const onShelf = !!line.lotId;
  const save = useMutation({
    mutationFn: () =>
      put<PurchaseView>(`/api/purchase-lines/${line.id}`, {
        purchaseId: p.id,
        formularyItemId: v.formularyItemId,
        productName: v.productName,
        supplierProductId: v.supplierProductId,
        strengthPerMl: v.strengthPerMl,
        vialSizeMl: v.vialSizeMl,
        quantity: v.quantity,
        unitCents: v.unitCents,
        lotNumber: v.lotNumber,
        expiresOn: v.expiresOn || null,
      }),
    onSuccess: onDone,
  });
  const remove = useMutation({ mutationFn: () => del<PurchaseView>(`/api/purchase-lines/${line.id}`), onSuccess: onDone });
  return (
    <Dialog
      title={`Edit line: ${line.name}`}
      onClose={onClose}
      footer={
        <>
          {!onShelf && (
            <Button variant="quiet" busy={remove.isPending} onClick={() => confirm("Remove this line?") && remove.mutate()}>
              Remove line
            </Button>
          )}
          <span className="flex-1" />
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {(save.error || remove.error) && <Alert>{errorMessage(save.error ?? remove.error)}</Alert>}
        <Field label="Supplier's name for it" maxLength={200} value={v.productName} onChange={(e) => setV({ ...v, productName: e.target.value })} />
        <Field label="Supplier's item #" maxLength={60} value={v.supplierProductId} onChange={(e) => setV({ ...v, supplierProductId: e.target.value })} />
        <Field label="Lot number" maxLength={60} value={v.lotNumber} onChange={(e) => setV({ ...v, lotNumber: e.target.value })} />
        <Field label="BUD / expiration" type="date" value={v.expiresOn} onChange={(e) => setV({ ...v, expiresOn: e.target.value })} />
        <MoneyField label="Price each" optional cents={v.unitCents} onChange={(c) => setV({ ...v, unitCents: c })} />
        <Field label="Quantity" type="number" min={0} step="any" disabled={onShelf} value={v.quantity} onChange={(e) => setV({ ...v, quantity: Number(e.target.value) })} />
        <Field label="Strength per mL" type="number" min={0} step="any" disabled={onShelf} value={v.strengthPerMl ?? ""} onChange={(e) => setV({ ...v, strengthPerMl: e.target.value === "" ? null : Number(e.target.value) })} />
        <Field label="Vial size (mL)" type="number" min={0} step="any" disabled={onShelf} value={v.vialSizeMl ?? ""} onChange={(e) => setV({ ...v, vialSizeMl: e.target.value === "" ? null : Number(e.target.value) })} />
        {onShelf && <p className="text-sm text-muted sm:col-span-2">These vials are on the shelf: lot and BUD changes update the lot too. Fix quantity, strength, or vial size on the lot (Inventory).</p>}
      </div>
    </Dialog>
  );
}

/** Reports → Purchases. */
export function purchaseTables(r: PurchasesReport): ReportTable[] {
  return [
    {
      title: "Purchases",
      columns: ["Date", "Supplier", "Invoice", "Order", "Product", "Supplier's name", "Item #", "Strength/mL", "Vial mL", "Qty", "Price", "Line total", "Landed price", "Landed total", "Lot", "BUD"],
      rows: r.lines.map((l) => [
        l.date,
        l.supplier,
        l.invoiceNumber,
        l.orderNumber,
        l.name,
        l.productName,
        l.supplierProductId,
        l.strengthPerMl,
        l.vialSizeMl,
        l.quantity,
        l.unitCents === null ? null : dollars(l.unitCents),
        l.totalCents === null ? null : dollars(l.totalCents),
        l.landedUnitCents === null ? null : dollars(Math.round(l.landedUnitCents)),
        l.landedTotalCents === null ? null : dollars(Math.round(l.landedTotalCents)),
        l.lotNumber,
        l.expiresOn ?? "",
      ]),
    },
    {
      title: "By supplier",
      columns: ["Supplier", "Orders", "Lines", "Shipping", "Fees", "Tax", "Discounts", "Total charged"],
      rows: r.bySupplier.map((s) => [s.supplier, s.orders, dollars(s.linesCents), dollars(s.shippingCents), dollars(s.feesCents), dollars(s.taxCents), dollars(s.discountCents), dollars(s.totalCents)]),
    },
    { title: "By product", columns: ["Product", "Quantity", "Spent", "Landed cost"], rows: r.byItem.map((i) => [i.name, i.quantity, dollars(i.totalCents), dollars(Math.round(i.landedCents))]) },
  ];
}

export function PurchasesReportView({ r }: { r: PurchasesReport }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end gap-2">
        <ExportButtons name={`Purchases ${r.from} to ${r.to}`} tables={() => purchaseTables(r)} />
      </div>
      <Panel className="overflow-x-auto">
        <PanelHeader title="By supplier" />
        <table className="table-base text-sm">
          <thead>
            <tr>
              <th>Supplier</th>
              <th className="text-right">Orders</th>
              <th className="text-right">Lines</th>
              <th className="text-right">Shipping, fees, tax</th>
              <th className="text-right">Discounts</th>
              <th className="text-right">Total charged</th>
            </tr>
          </thead>
          <tbody>
            {r.bySupplier.map((s) => (
              <tr key={s.supplier}>
                <td>{s.supplier}</td>
                <td className="text-right tabular-nums">{s.orders}</td>
                <td className="text-right tabular-nums">{s.linesCents ? money(s.linesCents) : "—"}</td>
                <td className="text-right tabular-nums">{money(s.shippingCents + s.feesCents + s.taxCents)}</td>
                <td className="text-right tabular-nums">{s.discountCents ? `−${money(s.discountCents)}` : "—"}</td>
                <td className="text-right font-semibold tabular-nums">{s.totalCents ? money(s.totalCents) : "—"}</td>
              </tr>
            ))}
            {!r.bySupplier.length && (
              <tr>
                <td colSpan={6} className="text-muted">
                  No purchases in this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Panel>
      <Panel className="overflow-x-auto">
        <PanelHeader title="Every line" description="Dated by order date." />
        <table className="table-base text-sm">
          <thead>
            <tr>
              <th>Date</th>
              <th>Supplier</th>
              <th>Product</th>
              <th>Strength × vial</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Total</th>
              <th className="text-right" title="Price plus its share of shipping, fees, and tax, less discounts">Landed</th>
              <th>Lot</th>
              <th>BUD</th>
            </tr>
          </thead>
          <tbody>
            {r.lines.map((l, i) => (
              <tr key={i}>
                <td className="whitespace-nowrap">{fmtDate(l.date)}</td>
                <td className="whitespace-nowrap">{l.supplier}</td>
                <td>{l.name}</td>
                <td className="whitespace-nowrap">{strengthText(l) || "—"}</td>
                <td className="text-right tabular-nums">{fmtNum(l.quantity)}</td>
                <td className="text-right tabular-nums">{l.totalCents === null ? "—" : money(l.totalCents)}</td>
                <td className="text-right tabular-nums">{l.landedTotalCents === null ? "—" : money(Math.round(l.landedTotalCents))}</td>
                <td className="whitespace-nowrap">{l.lotNumber || "—"}</td>
                <td className="whitespace-nowrap">{l.expiresOn ? fmtDate(l.expiresOn) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
